/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import type { HostContext, Logger, RootState } from '../../../@types';
import {
	setExactTypedSearchQuery,
	setSearchQuery,
	setSourceOfQuery,
	setSubmittedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
} from '../../../actions/searchActions';
import SearchUtils from '../../../utils/SearchUtils';
import Search from '@react/react-spectrum/Icon/Search';
import SearchService from '../../../services/SearchService';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import SearchHostContext from '../../../utils/SearchHostContext';
import './SearchDropdownAutoCompleteItem.scss';
type Props = {
	searchHostContext: HostContext;
	logger: Logger;
	onSubmit: (...args: Array<any>) => any;
	item: { name: string; source: string };
	index: number;
	showSearchDropdown: (...args: Array<any>) => any;
	searchQuery: string;
	searchQueryValue: string;
	setTempSearchQuery: (...args: Array<any>) => any;
	setSearchQuery: (...args: Array<any>) => any;
	setExactTypedSearchQuery: (...args: Array<any>) => any;
	setSubmittedSearchQuery: (...args: Array<any>) => any;
	searchHistoryItems: Array<string>;
	hostShowSearchDropdown: boolean;
} & WrappedComponentProps &
	PropsFromRedux;
/*
 * SearchAutoCompleteItem for Search.
 */

export class SearchAutoCompleteItem extends Component<Props> {
	state: any = {
		rendering: true,
		autoCompleteNode: null,
	};

	componentDidMount() {
		const element = this.getSearchQueryBold();
		if (element) {
			this.setState({ rendering: false, autoCompleteNode: element });
		}
	}

	/*
	 * Executes the Search Auto Suggestion Item Search Query
	 */
	async executeQuery(index: number, item: { name: string; source: string }, exactTypedSearchQuery: string) {
		this.props.setSourceOfQuery(item.source);
		const trimmedSearchQuery = item.name.trim();
		SearchAnalytics.searchType = SearchAnalytics.autoComplete;
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		analytics.initSearchAnalytics(trimmedSearchQuery);
		analytics.postDropDownSelection(index, exactTypedSearchQuery, item.source);
		this.props.setTempSearchQuery('');
		this.props.setSearchQuery(trimmedSearchQuery);
		this.props.setExactTypedSearchQuery(exactTypedSearchQuery);
		this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
		this.props.setSubmittedSearchQuery(true);
		// Save to local storage in case the refresh the results page
		this.props.onSubmit(trimmedSearchQuery);
		this.props.logger.info('Search Auto Suggestion', 'Logged Search Query', trimmedSearchQuery);
		SearchUtils.setSearchHistoryWithSearchQuery(this.props.searchHistoryItems, trimmedSearchQuery);

		try {
			const authenticated = SearchHostContext.isAuthenticated();
			if (
				authenticated &&
				this.props.searchHostContext.featureFlags.includes('temp_enable_search_history_server') &&
				trimmedSearchQuery !== ''
			) {
				const searchService = new SearchService(this.props.searchHostContext);
				await searchService.pushSearchHistory(trimmedSearchQuery);
			}
		} catch (e) {
			this.props.logger.error(
				'Search Auto Suggestion Item',
				'Problem creating/updating search history to search server on search auto suggestion click',
				e,
			);
		}
	}

	/*
	 * Returns the search query elements with bold charecters
	 * @return <element>
	 */
	getSearchQueryBold() {
		let searchQuery = this.props.searchQueryValue;
		searchQuery = searchQuery.toLocaleLowerCase();
		const textArray = SearchUtils.getSearchQueryItemSplitArray(this.props.item.name, searchQuery);
		return (
			<span className='search-dropdown-text-bold'>
				{textArray.map((item) => (
					<>{item === searchQuery ? <b>{item}</b> : item}</>
				))}
			</span>
		);
	}

	render() {
		const { rendering, autoCompleteNode } = this.state;
		return rendering ? null : (
			<div
				className='search-dropdown-auto-complete-item'
				id={`search-dropdown-auto-complete-item-${this.props.index}`}
				tabIndex={-1}
				data-ccs-query={this.props.item?.name}
				onClick={() => this.executeQuery(this.props.index, this.props.item, this.props.searchQuery)}
				role='option'
			>
				<div className='search-dropdown-auto-complete-item-text-container'>
					<div className='search-dropdown-auto-complete-item-auto-complete-icon-container'>
						<Search className='search-dropdown-auto-complete-item-auto-complete-icon' />
					</div>
					<div className='search-dropdown-auto-complete-item-text'>{autoCompleteNode}</div>
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	searchHistoryItems: search.searchHistoryItems,
	searchQueryValue: search.searchQuery,
	searchAutoCompleteItemsValue: search.searchAutoCompleteItems,
});

const mapDispatchToProps = {
	setSubmittedSearchQuery,
	setSearchQuery,
	setExactTypedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
	setSourceOfQuery,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchAutoCompleteItem));
